import { runInAction, toJS } from "mobx";
import posthog from "posthog-js";
import { useContext } from "react";
import { v4 } from "uuid";

import { usePlayground } from "../../playground-store";
import { PlaygroundDialogType } from "../../PlaygroundDialog/constants";
import { PlaygroundDialogStoreContext } from "../../PlaygroundDialog/playground-dialog-store";

import { usePlaygroundChannel } from "./usePlaygroundChannel";

import { ENDPOINTS } from "@/api/application-api";
import { Message } from "@/components/Messaging/types";
import { Message as TemplateMessage } from "@/components/Playground/types";
import type { IndividualRun } from "@/types/metadata";
import { WEBSOCKET_MESSAGE_NAME } from "@/constants";
import { useAuth } from "@/context/auth-context";
import { authHeader } from "@/utils/headers";
import { displayErrorToast } from "@/utils/toast";

interface IndividualRunRequestResponse {
  success: boolean;
  individual_run: IndividualRun & {
    message?: TemplateMessage | TemplateMessage[];
  };
}

const useRunCompleteListener = () => {
  const auth = useAuth();
  const playground = usePlayground();
  const playgroundDialogStore = useContext(PlaygroundDialogStoreContext);

  const handleRunComplete = async (payload: Message) => {
    const { individual_run_request_id }: { individual_run_request_id: number } = JSON.parse(
      payload.data,
    );

    const operation = runInAction(() =>
      playground.operationStore.getAssociatedOperationByRequestId(individual_run_request_id),
    );

    if (!operation) {
      return;
    }

    let output = runInAction(() => playground.getOutput(operation.output_instance_id!));

    try {
      const response = await fetch(
        new URL(
          `${ENDPOINTS.individual_run_requests}/${toJS(individual_run_request_id)}`,
        ).toString(),
        { headers: authHeader(auth?.userToken ?? "") },
      );

      const { individual_run, success }: IndividualRunRequestResponse = await response.json();

      if (individual_run?.status === "cancelled") {
        runInAction(() => {
          playground.operationStore.deleteOperation(operation.id);
        });
        return;
      }

      runInAction(() => {
        if (!operation.output_instance_id) {
          playground.operationStore.assignOutputId(operation.id, v4());
        }

        if (!output) {
          playground.addOutput(playground.clone(operation));
          // Re-fetch output after adding it
          output = playground.getOutput(operation.output_instance_id!);
        }

        output?.setOperationMessageCompleted(individual_run_request_id);
        output?.markRunDetailsFetched();

        // Auto-execution case: append all-but-last to the template; the last is rendered in the output card.
        if (Array.isArray(individual_run?.message) && individual_run.message.length > 1) {
          const messagesToAppend = individual_run.message.slice(0, -1);
          messagesToAppend.forEach((msg: TemplateMessage) => {
            playground.addMessage(msg);
          });
        }

        output?.setOperationRun(individual_run);

        if (!success) {
          posthog.capture("Playground Run Fail");
          displayErrorToast("Run failed");
          return;
        }

        if (individual_run?.status === "failed") {
          posthog.capture("Playground Run Fail");
          playground.operationStore.assignError(
            operation.id,
            individual_run_request_id,
            individual_run.status_message ?? "Run failed",
          );
          output?.setCurrentOperation(operation);
        } else {
          posthog.capture("Playground Run Success");
        }
      });
    } catch {
      runInAction(() => output?.markRunDetailsFetched());
      displayErrorToast("Failed to fetch run details");
    }

    // Wrap final state updates in runInAction
    runInAction(() => {
      if (!output || output.isOutputCompleted) {
        const hasError = Object.values(output?.currentOperation?.sets || {}).some(set => set.error);
        if (
          localStorage.getItem("shouldPromptToSaveAPrompt") === "true" &&
          playground.hasBeenEdited &&
          playgroundDialogStore &&
          !hasError
        ) {
          setTimeout(() => {
            localStorage.setItem("shouldPromptToSaveAPrompt", "false");
            playgroundDialogStore.setIsDialogOpen({
              isOpen: true,
              dialogType: PlaygroundDialogType.PromptToSaveVersion,
            });
          }, 1000);
        }

        playground.operationStore.deleteOperation(operation.id);
      }
    });
  };

  usePlaygroundChannel(WEBSOCKET_MESSAGE_NAME.INDIVIDUAL_RUN_COMPLETE, handleRunComplete);
};

export default useRunCompleteListener;
